'use client';

import { motion } from 'framer-motion';

type SkeletonProps = {
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  variant?: 'default' | 'circular' | 'rectangular';
};

const Skeleton = ({ 
  className = '', 
  style = {}, 
  children, 
  variant = 'rectangular' 
}: SkeletonProps) => {
  const baseClasses = 'animate-pulse bg-gray-200';
  const variantClasses = {
    default: 'rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-none'
  };

  return (
    <div
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      style={style}
    >
      {children}
    </div>
  );
};

// Generic skeleton components for different use cases
export const SkeletonText = ({ lines = 1, className = '' }: { lines?: number; className?: string }) => (
  <div className={`space-y-2 ${className}`}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton 
        key={i} 
        className={`h-4 ${i === lines - 1 ? 'w-3/4' : 'w-full'}`} 
      />
    ))}
  </div>
);

export const SkeletonCard = ({ className = '' }: { className?: string }) => (
  <div className={`p-4 border rounded-lg ${className}`}>
    <Skeleton className="h-6 w-3/4 mb-2" />
    <SkeletonText lines={3} className="mb-4" />
    <Skeleton className="h-8 w-24 rounded-full" />
  </div>
);

export const SkeletonButton = ({ className = '' }: { className?: string }) => (
  <Skeleton className={`h-8 w-20 rounded-full ${className}`} />
);

export const SkeletonAvatar = ({ size = 'md', className = '' }: { size?: 'sm' | 'md' | 'lg'; className?: string }) => {
  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12'
  };
  
  return <Skeleton variant="circular" className={`${sizeClasses[size]} ${className}`} />;
};

// Specific skeleton for header component
export const HeaderLoadingSkeleton = () => {
  return (
    <motion.div
      className="w-full"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      {/* Top Bar Skeleton */}
      <div className="py-2 px-4" style={{ backgroundColor: '#B5CEFC' }}>
        <div className="max-w-7xl mx-auto flex justify-between items-center text-sm">
          <Skeleton className="h-4 w-24" />
          <div className="flex items-center space-x-4">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-8" />
          </div>
        </div>
      </div>

      {/* Main Navigation Skeleton */}
      <div
        className="shadow-sm mx-4 mt-4"
        style={{
          borderRadius: '70px',
          background: '#F5F6FF',
          backdropFilter: 'blur(20px)'
        }}
      >
        <div
          className="flex justify-between items-center h-16 px-4 sm:px-6 lg:px-8"
          style={{
            borderRadius: '40px',
            background: '#F5F6FF'
          }}
        >
          {/* Logo Skeleton */}
          <div className="flex items-center space-x-3">
            <SkeletonAvatar size="md" />
            <div className="flex flex-col space-y-1">
              <Skeleton className="h-5 w-16" />
              <Skeleton className="h-4 w-12" />
            </div>
          </div>

          {/* Navigation Links Skeleton */}
          <div className="hidden md:flex items-center space-x-6">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-16" />
            <SkeletonButton />
            <SkeletonButton className="w-24" />
          </div>

          {/* Mobile Menu Button Skeleton */}
          <div className="md:hidden">
            <Skeleton className="h-6 w-6" />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default Skeleton;
